import React from 'react';
import style from './style.module.scss';
import { observer } from 'mobx-react';
import {
    Table,
    Button,
    Modal,
    Spin,
    Descriptions,
    Tooltip,
    Avatar,
    Popover,
    Space,
    Row,
    InputNumber,
} from 'antd';
import { BigModal } from '../../components/big-modal';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { SearchInput } from '../../components/search-input';
import { UserProfile } from '../../components/user-profile';
import { Tag } from '../../components/tag';
import { ColumnProps } from 'antd/es/table';
import {
    useAuthStore,
    useClientStore,
    useApplicationSelectionStore,
} from '../../contexts/mobx';
import { useSearchParams } from '../../utils/use-search-params';
import {
    AgeVerificationStatus,
    SearchParams,
    Client,
    FetchListParams,
} from '../../stores/client';
import MoreIcon from '../../icons/more-horizontal.svg?react';
import * as Colors from '../../styles/colors';
import TicketIcon from '../../icons/Ticket.svg?react';
import { UserCouponsTab } from '../../components/user-profile/user-coupons-tab';
import { Text } from '../../utils/text';
import { formatAppTargets } from '../../utils/app-target';
import moment from 'moment';
import useWindowDimensions from '../../utils/display';
import CheckIcon from '../../icons/CheckCircle.svg?react';
import EmptyCheckIcon from '../../icons/CheckCircleEmpty.svg?react';
import UnblockPaymentModal from './components/modals/UnblockPaymentModal';
import UnblockCarwashModal from './components/modals/UnblockCarwashModal';
import ResetPasswordModal from './components/modals/ResetPasswordModal';
import ValidateClientEmailModal from './components/modals/ValidateClientEmailModal';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import UpdateExternalIdentifiersModal from './components/modals/UpdateExternalIdentifiersModal';
import OnboardingModal from './components/modals/OnboardingModal';
import { useHistory, useLocation } from 'react-router-dom';
import PhoneValidationModal from './components/modals/PhoneValidationModal';
import EmailValidationModal from './components/modals/EmailValidationModal';

export const ClientsPage = observer(() => {
    const clientStore = useClientStore();
    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const { isManager, isCustomerServiceAgent } = authStore;
    const { hasManagementAccess } = authStore;
    const history = useHistory();
    const location = useLocation();

    const { isUpdatingConsentStatus } = clientStore;
    const search = useSearchParams<SearchParams>();

    const [showResetPwdModal, setResetPwdModal] = React.useState(false);
    const [showUnblockCarwashModal, setUnblockCarwashModal] = React.useState(
        false
    );
    const [
        showUnblockPaymentMethodsModal,
        setUnblockPaymentMethodsModal,
    ] = React.useState(false);
    const [validateEmailModal, setValidateEmailModal] = React.useState(false);
    const [selectedClient, setSelectedClient] = React.useState<Client | null>(
        null
    );
    const [searchValue, setSearchValue] = React.useState<string>();

    const [userProfile, setUserProfile] = React.useState(false);

    const [userProfileData, setUserProfileData] = React.useState<Client>();

    const [paymentObject, setPaymentObject] = React.useState({
        loading: clientStore.isFetchingPaymentMethod,
        data: clientStore.paymentMethod,
    });

    const [deviceAppVersion, setDeviceAppVersion] = React.useState({
        loading: clientStore.isFetchingPaymentMethod,
        data: clientStore.deviceAppVersion,
    });

    const [clientCouponsModal, setClientCouponsModal] = React.useState(false);

    const [nhlGameModal, setNhlGameModal] = React.useState(false);

    const [generatePin, setGeneratePin] = React.useState(1);
    const [pins, setPins] = React.useState<string[]>();
    const [newPinsGenerated, setNewPinsGenerated] = React.useState(false);

    const [showDeleteClientModal, setDeleteClientModal] = React.useState(false);
    const [
        showResetAgeVerificationModal,
        setShowResetAgeVerificationModal,
    ] = React.useState(false);
    const [
        resetAgeVerificationClient,
        setResetAgeVerificationClient,
    ] = React.useState<Client | null>(null);
    const [
        showUpdateIdentifiersModal,
        setShowUpdateIdentifiersModal,
    ] = React.useState(false);

    const [showOnboardingModal, setShowOnboardingModal] = React.useState(false);
    const [
        showPhoneValidationModal,
        setShowPhoneValidationModal,
    ] = React.useState(false);
    const [
        showEmailValidationModal,
        setShowEmailValidationModal,
    ] = React.useState(false);
    const [showTesterStatusModal, setShowTesterStatusModal] = React.useState(
        false
    );
    const [
        testerStatusClient,
        setTesterStatusClient,
    ] = React.useState<Client | null>(null);

    const firstRender = React.useRef(false);

    const fetchListParams = React.useMemo<FetchListParams>(
        () => ({
            ...search.params,
            appTarget: applicationSelectionStore.appTarget,
        }),
        [search.params, applicationSelectionStore.appTarget]
    );

    React.useEffect(() => {
        clientStore.fetchList(fetchListParams);
        setSearchValue(search.params.q);
    }, [clientStore, fetchListParams, search.params.q]);

    React.useEffect(() => {
        if (clientStore.isSendingResetEmail === false) {
            setResetPwdModal(false);
        }
    }, [clientStore.isSendingResetEmail]);

    React.useEffect(() => {
        if (clientStore.isUnblockingDevice === false) {
            setUnblockCarwashModal(false);
        }
    }, [clientStore.isUnblockingDevice]);

    React.useEffect(() => {
        if (firstRender.current) {
            if (userProfileData) {
                clientStore.getPaymentMethod({ clientId: userProfileData.id });
                clientStore.fetchClientDeviceAppVersion(userProfileData.id);
            }
        } else {
            firstRender.current = true;
        }
    }, [userProfileData, clientStore]);

    React.useEffect(() => {
        setPaymentObject({
            loading: clientStore.isFetchingPaymentMethod,
            data: clientStore.paymentMethod,
        });
        setDeviceAppVersion({
            loading: clientStore.isFetchingPaymentMethod,
            data: clientStore.deviceAppVersion,
        });
    }, [
        clientStore.deviceAppVersion,
        clientStore.isFetchingPaymentMethod,
        clientStore.paymentMethod,
    ]);

    React.useEffect(() => {
        if (userProfileData) {
            clientStore.fetchUserActiveConsentsWithStatus(userProfileData.id);
        }
    }, [userProfileData, clientStore]);

    const handleUpdateConsentStatus = (
        userConsentRecordId: number,
        status: string
    ) => {
        if (userProfileData) {
            clientStore.updateUserConsentStatus(
                userProfileData.id,
                userConsentRecordId,
                status
            );
        }
    };

    const closeUserProfile = () => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('washCodePage');
        searchParams.delete('washCodePerPage');

        history.replace({
            pathname: location.pathname,
            search: searchParams.toString(),
        });

        setUserProfile((current) => !current);
    };

    const showUserProfile = (user: Client) => {
        setUserProfileData(user);
        !userProfile && setUserProfile((current) => !current);
    };

    const handleResetPassword = () => {
        if (!userProfileData?.email) {
            Modal.error({ title: "This client doesn't have an email" });
        } else {
            setSelectedClient(userProfileData);
            setResetPwdModal(true);
        }
    };

    const unlockCarwash = React.useCallback((client: Client) => {
        if (!client.liquidBarcodeId) {
            Modal.error({
                title: "This client doesn't have an Liquid Barcode Id",
            });
        }
        if (client.liquidBarcodeId) {
            if (!client.carwashDeviceLockedUntil) {
                Modal.error({
                    title: 'This client is already unlocked',
                });
            } else {
                setSelectedClient(client);
                setUnblockCarwashModal(true);
            }
        }
    }, []);

    const unlockPaymentMethodAction = React.useCallback(
        async (client: Client) => {
            setSelectedClient(client);
            setUnblockPaymentMethodsModal(true);
        },
        []
    );

    const handleDeleteClient = React.useCallback(async (client: Client) => {
        setSelectedClient(client);
        setDeleteClientModal(true);
    }, []);

    const handleClientCoupons = React.useCallback((client: Client) => {
        setClientCouponsModal(true);
        setUserProfileData(client);
    }, []);

    const handleNhlGame = React.useCallback(async (client: Client) => {
        const isPlayingNhlGame = await clientStore.getNhlGameProfil({
            clientId: client.id,
        });

        if (isPlayingNhlGame) {
            setNhlGameModal(true);
            setUserProfileData(client);
        }
    }, []);

    const generateSacPin = async (clientId: number) => {
        const newPins = await clientStore.createSacPins(clientId, generatePin);

        if (newPins) {
            setNewPinsGenerated(true);
            setPins(newPins.pins);
        }
    };

    const validateEmail = React.useCallback((client: Client) => {
        if (!client.email) {
            Modal.error({
                title: "This client doesn't have an email",
            });
        } else {
            setSelectedClient(client);
            setValidateEmailModal(true);
        }
    }, []);

    const handlePhoneValidationAction = React.useCallback((client: Client) => {
        if (!client.phoneNumber) {
            Modal.error({
                title: "This client doesn't have a phone number",
                content:
                    'Phone validation requires a phone number to be present.',
            });
            return;
        }
        setSelectedClient(client);
        setShowPhoneValidationModal(true);
    }, []);

    const handleEmailValidationAction = React.useCallback((client: Client) => {
        if (!client.email) {
            Modal.error({
                title: "This client doesn't have an email",
            });
        } else {
            setSelectedClient(client);
            setShowEmailValidationModal(true);
        }
    }, []);

    const handleOnboardingAction = React.useCallback((client: Client) => {
        setSelectedClient(client);
        setShowOnboardingModal(true);
    }, []);

    const columns = React.useMemo<ColumnProps<Client>[]>(
        () => [
            {
                title: 'Id',
                dataIndex: 'id',
                key: 'id',
                width: 80,
                ellipsis: true,
            },
            {
                title: 'Name',
                key: 'name',
                width: 150,
                ellipsis: true,
                render: (_: unknown, record: Client) => (
                    <>
                        <Avatar
                            shape="circle"
                            style={{
                                verticalAlign: 'middle',
                                backgroundColor: Colors.RED_500,
                            }}
                            size={30}
                            src={record.picture!}
                        >
                            {record.firstName?.charAt(0).toLocaleUpperCase()}
                            {record.lastName?.charAt(0).toLocaleUpperCase()}
                        </Avatar>
                        <Tooltip
                            placement="topLeft"
                            title={`${record.firstName} ${record.lastName}`}
                        >
                            <span className={style.name}>
                                {record.firstName} {record.lastName}
                            </span>
                        </Tooltip>
                    </>
                ),
            },
            {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
                width: 150,
                ellipsis: true,
                render: (_: unknown, record: Client) => (
                    <div className={style.contact_info}>
                        {record.email && (
                            <div className={style.confirmed}>
                                {record.isEmailConfirmed ? (
                                    <CheckIcon />
                                ) : (
                                    <EmptyCheckIcon stroke="grey" />
                                )}
                            </div>
                        )}
                        <Tooltip placement="topLeft" title={record.email}>
                            <span>{record.email}</span>
                        </Tooltip>
                    </div>
                ),
            },
            {
                title: 'App target',
                key: 'appTargets',
                width: 120,
                ellipsis: true,
                render: (_: unknown, record: Client) => {
                    const labels = formatAppTargets(record.appTargets);
                    return (
                        <Tooltip placement="topLeft" title={labels}>
                            <span>{labels}</span>
                        </Tooltip>
                    );
                },
            },
            {
                title: (
                    <Tooltip placement="topLeft" title={'Language'}>
                        <span>Language</span>
                    </Tooltip>
                ),
                key: 'language',
                width: 60,
                ellipsis: true,
                render: (_: unknown, record: Client) => (
                    <Tag variant={record.language === 'fr' ? 'error' : 'gold'}>
                        {record.language}
                    </Tag>
                ),
            },
            {
                title: (
                    <Tooltip placement="topLeft" title={'Liquid Barcodes ID'}>
                        <span>Liquid Barcodes ID</span>
                    </Tooltip>
                ),
                key: 'liquidBarcodeId',
                width: 100,
                ellipsis: true,
                render: (_, record) => (
                    <Tooltip placement="topLeft" title={record.liquidBarcodeId}>
                        <span>{record.liquidBarcodeId}</span>
                    </Tooltip>
                ),
            },
            {
                title: 'NGRP ID',
                key: 'ngrpCustomerId',
                width: 100,
                ellipsis: true,
                render: (_, record) => (
                    <Tooltip placement="topLeft" title={record.ngrpCustomerId}>
                        <span>{record.ngrpCustomerId}</span>
                    </Tooltip>
                ),
            },
            {
                title: 'Birthday',
                key: 'birthday',
                width: 100,
                ellipsis: true,
                render: (_, record) => (
                    <Tooltip
                        placement="topLeft"
                        title={record.birthday?.format('LL')}
                    >
                        <span>{record.birthday?.format('LL')}</span>
                    </Tooltip>
                ),
            },
            {
                title: (
                    <Tooltip placement="topLeft" title={'Postal code'}>
                        <span>Postal code</span>
                    </Tooltip>
                ),
                key: 'postalCode',
                width: 100,
                ellipsis: true,
                dataIndex: 'postalCode',
            },
            {
                title: 'Phone',
                key: 'phoneNumber',
                dataIndex: 'phoneNumber',
                width: 130,
                ellipsis: true,
                render: (_, record) => (
                    <div className={style.contact_info}>
                        {record.phoneNumber && (
                            <div className={style.confirmed}>
                                {record.isPhoneNumberConfirmed ? (
                                    <CheckIcon />
                                ) : (
                                    <EmptyCheckIcon stroke="grey" />
                                )}
                            </div>
                        )}
                        <Tooltip placement="topLeft" title={record.phoneNumber}>
                            <span>{record.phoneNumber}</span>
                        </Tooltip>
                    </div>
                ),
            },
            {
                key: 'more_options',
                width: 80,
                render: (_, record) => (
                    <div className={style.actions}>
                        <Tooltip
                            placement="top"
                            title={Text.clientsPage.reissueButton}
                        >
                            <div className={style.reissue}>
                                <button
                                    className={style.reissue_button}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClientCoupons(record);
                                    }}
                                >
                                    <TicketIcon />
                                </button>
                            </div>
                        </Tooltip>
                        <Popover
                            placement="left"
                            content={
                                <Space direction="vertical" size="small">
                                    {isManager && (
                                        <>
                                            <span
                                                className={style.clickable_span}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedClient(record);
                                                    setShowUpdateIdentifiersModal(
                                                        true
                                                    );
                                                }}
                                            >
                                                Update External Identifiers
                                            </span>
                                            <div
                                                className={style.divider}
                                            ></div>
                                            <span
                                                className={style.clickable_span}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTesterStatusClient(
                                                        record
                                                    );
                                                    setShowTesterStatusModal(
                                                        true
                                                    );
                                                }}
                                            >
                                                Toggle Tester Status
                                            </span>
                                            <div
                                                className={style.divider}
                                            ></div>
                                        </>
                                    )}

                                    {(hasManagementAccess ||
                                        isCustomerServiceAgent) && (
                                        <>
                                            <span
                                                className={style.clickable_span}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOnboardingAction(
                                                        record
                                                    );
                                                }}
                                            >
                                                Onboarding
                                            </span>
                                            <div
                                                className={style.divider}
                                            ></div>
                                        </>
                                    )}

                                    <span
                                        className={style.clickable_span}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            unlockCarwash(record);
                                        }}
                                    >
                                        {Text.clientsPage.quickAction.carwash}
                                    </span>
                                    <div className={style.divider}></div>
                                    <span
                                        className={style.clickable_span}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            validateEmail(record);
                                        }}
                                    >
                                        {Text.clientsPage.quickAction.email}
                                    </span>
                                    <div className={style.divider}></div>
                                    <span
                                        className={style.clickable_span}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleNhlGame(record);
                                        }}
                                    >
                                        {Text.clientsPage.quickAction.pins}
                                    </span>
                                    <div className={style.divider}></div>
                                    <span
                                        className={style.clickable_span}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            unlockPaymentMethodAction(record);
                                        }}
                                    >
                                        Unblock Payment Methods
                                    </span>
                                    <div className={style.divider}></div>
                                    <span
                                        className={style.clickable_span}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePhoneValidationAction(record);
                                        }}
                                    >
                                        Force Phone Number Validation
                                    </span>
                                    <div className={style.divider}></div>
                                    <span
                                        className={style.clickable_span}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEmailValidationAction(record);
                                        }}
                                    >
                                        Force Email Validation
                                    </span>
                                    {(hasManagementAccess ||
                                        isCustomerServiceAgent) && (
                                        <>
                                            <div
                                                className={style.divider}
                                            ></div>
                                            <span
                                                className={style.clickable_span}
                                                style={
                                                    record.ageVerificationStatus ===
                                                        AgeVerificationStatus.UNVERIFIED ||
                                                    !record.ageVerificationStatus
                                                        ? {
                                                              opacity: 0.5,
                                                              pointerEvents:
                                                                  'none',
                                                          }
                                                        : undefined
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setResetAgeVerificationClient(
                                                        record
                                                    );
                                                    setShowResetAgeVerificationModal(
                                                        true
                                                    );
                                                }}
                                            >
                                                Reset age verification
                                            </span>
                                        </>
                                    )}
                                    <div className={style.divider}></div>
                                    <span
                                        className={style.clickable_span}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteClient(record);
                                        }}
                                    >
                                        Delete Client
                                    </span>
                                </Space>
                            }
                            overlayClassName={style.popover_overlay}
                            title={null}
                            trigger="click"
                        >
                            <Button
                                className={style.more_options}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                <MoreIcon />
                            </Button>
                        </Popover>
                    </div>
                ),
            },
        ],
        [isManager, hasManagementAccess, isCustomerServiceAgent]
    );

    const handleSearchClick = () =>
        search.setParams({ q: searchValue, page: 0 });
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        e.target.value !== ''
            ? setSearchValue(e.target.value)
            : (search.setParams({ q: e.target.value, page: 0 }),
              setSearchValue(e.target.value));
    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
        e.key === 'Enter' && search.setParams({ q: searchValue, page: 0 });

    const { windowHeight } = useWindowDimensions();

    return (
        <>
            <PageTopBar title="Clients" />

            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <div className={'query_box'}>
                        <SearchInput
                            searchValue={searchValue}
                            onClickIcon={handleSearchClick}
                            onChange={handleOnChange}
                            onKeyDown={handleOnKeyDown}
                        />
                    </div>
                </div>
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: clientStore.list?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            <Table<Client>
                className="clickable_table_body_rows"
                rowKey="id"
                columns={columns}
                scroll={{ y: windowHeight - 150, x: 900 }}
                dataSource={clientStore.list?.results}
                loading={clientStore.fetchingCount > 0}
                bordered
                pagination={false}
                onRow={(r) => ({
                    onClick: () => showUserProfile(r),
                })}
            />

            <UnblockCarwashModal
                isVisible={showUnblockCarwashModal}
                clientId={selectedClient?.id}
                deviceFirebaseId={selectedClient?.carwashSubscriptionDevice}
                deviceLockedUntil={selectedClient?.carwashDeviceLockedUntil}
                onUnblock={(id) => clientStore.unblockCarwashDevice(id)}
                onClose={() => setUnblockCarwashModal(false)}
                isUnblocking={clientStore.isUnblockingDevice}
            />

            <ResetPasswordModal
                isVisible={showResetPwdModal}
                clientEmail={selectedClient?.email}
                onSend={(email) => clientStore.sendResetPasswordEmail(email)}
                onClose={() => setResetPwdModal(false)}
                isSending={clientStore.isSendingResetEmail}
            />

            <UnblockPaymentModal
                isVisible={showUnblockPaymentMethodsModal}
                client={selectedClient}
                onUnblock={(clientId) => {
                    clientStore.unblockPaymentMethod(clientId);
                    setUnblockPaymentMethodsModal(false);
                }}
                onClose={() => setUnblockPaymentMethodsModal(false)}
            />

            <ValidateClientEmailModal
                isVisible={validateEmailModal}
                clientId={selectedClient?.id}
                clientEmail={selectedClient?.email}
                isSending={clientStore.isSendingValidationEmail}
                onValidate={(id) => clientStore.validateEmail(id)}
                onClose={() => setValidateEmailModal(false)}
            />

            <ConfirmationModal
                isVisible={showDeleteClientModal}
                title="Delete Client"
                description={
                    <>
                        <Descriptions column={1}>
                            <Descriptions.Item label="Client ID">
                                {selectedClient?.id}
                            </Descriptions.Item>
                            <Descriptions.Item label="Name">
                                {selectedClient?.firstName}{' '}
                                {selectedClient?.lastName}
                            </Descriptions.Item>
                        </Descriptions>
                        <p>
                            You are about to delete the client's record from the
                            system. This action is irreversible.
                        </p>
                        <p>
                            <b>Note:</b> Ensure that you have verified the
                            client's identity and intent before proceeding.
                        </p>
                        <p>
                            A client with an active carwash subscription cannot
                            be deleted. Cancel the subscription via the{' '}
                            <b>Liquid Barcode dashboard</b> first, then delete.
                        </p>
                    </>
                }
                confirmLabel="Confirm Delete"
                onConfirm={() => clientStore.deleteClient(selectedClient!.id)}
                onClose={() => setDeleteClientModal(false)}
                successMessage="Client successfully deleted from the database."
                errorMessage="Failed to delete the client."
            />

            <ConfirmationModal
                isVisible={showResetAgeVerificationModal}
                title="Reset Age Verification"
                description={
                    <>
                        <Descriptions column={1}>
                            <Descriptions.Item label="Client ID">
                                {resetAgeVerificationClient?.id}
                            </Descriptions.Item>
                            <Descriptions.Item label="Name">
                                {resetAgeVerificationClient?.firstName}{' '}
                                {resetAgeVerificationClient?.lastName}
                            </Descriptions.Item>
                        </Descriptions>
                        <p>
                            You are about to reset the age verification for this
                            client. This action is irreversible.
                        </p>
                    </>
                }
                confirmLabel="Reset"
                onConfirm={() =>
                    clientStore.resetAgeVerification(
                        resetAgeVerificationClient!.id
                    )
                }
                onClose={() => {
                    setShowResetAgeVerificationModal(false);
                    clientStore.fetchList(fetchListParams);
                }}
                successMessage="Age verification has been reset."
                errorMessage="Failed to reset age verification."
            />

            {isManager && (
                <ConfirmationModal
                    isVisible={showTesterStatusModal}
                    title="Toggle Tester Status"
                    description={
                        <>
                            <Descriptions column={1}>
                                <Descriptions.Item label="Client ID">
                                    {testerStatusClient?.id}
                                </Descriptions.Item>
                                <Descriptions.Item label="Name">
                                    {testerStatusClient?.firstName}{' '}
                                    {testerStatusClient?.lastName}
                                </Descriptions.Item>
                                <Descriptions.Item label="Current status">
                                    {testerStatusClient?.isTester
                                        ? 'Tester'
                                        : 'Not a tester'}
                                </Descriptions.Item>
                            </Descriptions>
                            <p>
                                You are about to{' '}
                                <b>
                                    {testerStatusClient?.isTester
                                        ? 'remove tester status from'
                                        : 'grant tester status to'}
                                </b>{' '}
                                this client.
                            </p>
                        </>
                    }
                    confirmLabel={
                        testerStatusClient?.isTester
                            ? 'Remove Tester'
                            : 'Grant Tester'
                    }
                    confirmVariant={
                        testerStatusClient?.isTester
                            ? 'secondary-destructive'
                            : 'secondary'
                    }
                    onConfirm={() =>
                        clientStore.updateTesterStatus(
                            testerStatusClient!.id,
                            !testerStatusClient!.isTester
                        )
                    }
                    onClose={() => {
                        setShowTesterStatusModal(false);
                        clientStore.fetchList(fetchListParams);
                    }}
                    successMessage="Tester status updated successfully."
                    errorMessage="Failed to update tester status."
                />
            )}

            <PhoneValidationModal
                isVisible={showPhoneValidationModal}
                clientId={selectedClient?.id}
                phoneNumber={selectedClient?.phoneNumber ?? undefined}
                isPhoneConfirmed={
                    selectedClient?.isPhoneNumberConfirmed ?? undefined
                }
                onClose={() => {
                    setShowPhoneValidationModal(false);
                    clientStore.fetchList(fetchListParams);
                }}
            />

            <EmailValidationModal
                isVisible={showEmailValidationModal}
                clientId={selectedClient?.id}
                clientEmail={selectedClient?.email}
                isEmailConfirmed={selectedClient?.isEmailConfirmed ?? false}
                isValidating={clientStore.isValidatingClientEmail}
                onValidate={async (id) => {
                    const result = await clientStore.validateClientEmail(id);
                    if (result) {
                        await clientStore.fetchList(fetchListParams);
                    }
                    return Promise.resolve();
                }}
                onClose={() => {
                    setShowEmailValidationModal(false);
                    clientStore.fetchList(fetchListParams);
                }}
            />

            {hasManagementAccess && (
                <UpdateExternalIdentifiersModal
                    isVisible={showUpdateIdentifiersModal}
                    client={selectedClient}
                    onClose={() => setShowUpdateIdentifiersModal(false)}
                />
            )}

            {(hasManagementAccess || isCustomerServiceAgent) && (
                <OnboardingModal
                    isVisible={showOnboardingModal}
                    clientId={selectedClient?.id}
                    onClose={() => setShowOnboardingModal(false)}
                />
            )}

            {nhlGameModal ? (
                clientStore.isFetchingNhlGameProfil ? (
                    <Spin />
                ) : (
                    <BigModal
                        onClose={() => {
                            setNhlGameModal(false);
                            setNewPinsGenerated(false);
                            setGeneratePin(1);
                            setPins(undefined);
                        }}
                        header={
                            <div className={style.coupons_header}>
                                <div className={style.header_description}>
                                    {Text.bigModal.molsonGame.registerd}{' '}
                                    {moment(
                                        clientStore.nhlGameProfil?.createdAt
                                    ).format('LLL')}
                                </div>
                                <div className={style.user_name}>
                                    {userProfileData?.firstName}{' '}
                                    {userProfileData?.lastName}
                                </div>
                            </div>
                        }
                    >
                        {newPinsGenerated ? (
                            <Descriptions
                                bordered
                                size="small"
                                title={Text.clientsPage.description.newPins}
                                className={style.row_style}
                                layout={'vertical'}
                            >
                                <Descriptions.Item
                                    label={
                                        Text.clientsPage.description
                                            .newPinsCreated
                                    }
                                >
                                    {pins
                                        ? pins.map((p) => (
                                              <span
                                                  key={p}
                                                  className={style.pins_style}
                                              >
                                                  {p}{' '}
                                              </span>
                                          ))
                                        : null}
                                </Descriptions.Item>
                            </Descriptions>
                        ) : (
                            <>
                                <Row
                                    justify="start"
                                    className={style.description_style}
                                >
                                    {Text.clientsPage.description.grantPins}
                                </Row>

                                <Row
                                    justify="start"
                                    className={style.description_style}
                                >
                                    {userProfileData ? (
                                        clientStore.isFetchingNhlGameProfilPins ? (
                                            <Spin />
                                        ) : (
                                            <>
                                                <InputNumber
                                                    min={1}
                                                    max={10}
                                                    defaultValue={1}
                                                    onChange={(e) => {
                                                        setGeneratePin(
                                                            Number(e)
                                                        );
                                                    }}
                                                />
                                                <Button
                                                    className={
                                                        style.description_style
                                                    }
                                                    onClick={() =>
                                                        generateSacPin(
                                                            userProfileData.id
                                                        )
                                                    }
                                                >
                                                    {
                                                        Text.clientsPage
                                                            .description.create
                                                    }
                                                </Button>
                                            </>
                                        )
                                    ) : null}{' '}
                                </Row>
                            </>
                        )}
                        <Descriptions
                            bordered
                            size="small"
                            title={`Participation #${clientStore.nhlGameProfil?.participationId}`}
                            className={style.row_style}
                            layout={'vertical'}
                        >
                            <Descriptions.Item
                                label={Text.clientsPage.description.team}
                            >
                                <Avatar
                                    src={
                                        clientStore.nhlGameProfil?.teamLogo ??
                                        undefined
                                    }
                                />{' '}
                                {clientStore.nhlGameProfil?.team}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={Text.clientsPage.description.points}
                            >
                                {clientStore.nhlGameProfil?.points}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={Text.clientsPage.description.rank}
                            >
                                {clientStore.nhlGameProfil?.rank}
                            </Descriptions.Item>
                        </Descriptions>
                        <Descriptions
                            bordered
                            size="small"
                            title={Text.clientsPage.description.predictions}
                            className={style.row_style}
                            layout={'vertical'}
                        >
                            <Descriptions.Item
                                label={
                                    Text.clientsPage.description
                                        .predictionsTotal
                                }
                            >
                                {
                                    clientStore.nhlGameProfil?.predictions
                                        .totalPredictions
                                }
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    Text.clientsPage.description
                                        .predictionsWithPins
                                }
                            >
                                {
                                    clientStore.nhlGameProfil?.predictions
                                        .predictionWithPins
                                }
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    Text.clientsPage.description
                                        .predictionsWithoutPins
                                }
                            >
                                {
                                    clientStore.nhlGameProfil?.predictions
                                        .predictionWithoutPins
                                }
                            </Descriptions.Item>
                        </Descriptions>
                        <Descriptions
                            bordered
                            size="small"
                            title={Text.clientsPage.description.pins}
                            className={style.row_style}
                            layout={'vertical'}
                        >
                            <Descriptions.Item
                                label={Text.clientsPage.description.totalPins}
                            >
                                {clientStore.nhlGameProfil?.pins.totalPins}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    Text.clientsPage.description.totalPinsUsed
                                }
                            >
                                {
                                    clientStore.nhlGameProfil?.pins
                                        .totalPinsBurned
                                }
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    Text.clientsPage.description
                                        .totalPinsPending
                                }
                            >
                                {
                                    clientStore.nhlGameProfil?.pins
                                        .totalPinsPending
                                }
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    Text.clientsPage.description
                                        .totalPinsCreatedSac
                                }
                            >
                                {clientStore.nhlGameProfil?.pins.totalPinsSac}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    Text.clientsPage.description
                                        .totalPinsValidated
                                }
                            >
                                {
                                    clientStore.nhlGameProfil?.pins
                                        .totalPinsValidated
                                }
                            </Descriptions.Item>
                        </Descriptions>
                    </BigModal>
                )
            ) : null}
            {clientCouponsModal && (
                <BigModal
                    onClose={() => setClientCouponsModal(false)}
                    header={
                        <div className={style.coupons_header}>
                            <div className={style.header_description}>
                                {Text.bigModal.reissueCoupon.headertext}
                                {userProfileData?.id}
                            </div>
                            <div className={style.user_name}>
                                {userProfileData?.firstName}{' '}
                                {userProfileData?.lastName}
                            </div>
                        </div>
                    }
                >
                    <UserCouponsTab clientObject={userProfileData} />
                </BigModal>
            )}
            <div
                className={
                    userProfile
                        ? `${style.user_profile} ${style.active}`
                        : `${style.user_profile}`
                }
            >
                <UserProfile
                    onCloseClick={closeUserProfile}
                    clientObject={userProfileData}
                    resetPassword={handleResetPassword}
                    paymentMethod={paymentObject}
                    deviceAppVersion={deviceAppVersion}
                    userConsents={clientStore.userConsentsWithStatus}
                    onUpdateConsentStatus={handleUpdateConsentStatus}
                    isUpdatingConsentStatus={isUpdatingConsentStatus}
                    hasManagementAccess={hasManagementAccess}
                />
            </div>
        </>
    );
});
